import React, { useEffect, useState, useRef } from "react";
import { Copy, Check, Info, Monitor, Printer, Paintbrush, Building, Palette, Eye, Settings, ChevronDown, ChevronUp } from 'lucide-react';

import {
  hexToRgb,
  hexToHsl,
  hexToLAB,
  getRelativeLuminance,
  getWadaColorName,
  rgbToCMYK,
  rgbToHSB,
  getClosestRAL,
  getClosestHKS,
  getClosestCOPIC,
  getClosestPANTONE,
  getClosestIKEA,
  getClosestPRISMACOLOR,
  getClosestMATERIALDESIGN,
  getClosestTAILWIND,
  getClosestNCS,
  getClosestFEDERALSTANDARD,
  getClosestBRITISHSTANDARD,
  hexToLCH,
  hexToOKLAB,
  hexToOKLCH,
  getColorTemperature,
  getClosestCSSColor,
  getDominantWavelength,
  rgbToXYZ,
  getPerceivedBrightness,
  getColorTemperatureType,
  getWCAGContrastRatio,
  getWCAGCompliance,
  getAccessibleAlternatives,
  getColorPsychology,
  getColorHarmonies,
  getClosestMUNSELL,
  getClosestAUTOMOTIVE,
  getClosestCRAYOLA,
  getClosestSHERWINWILLIAMS,
  getClosestBENJAMINMOORE,
  getClosestDULUX,
  getClosestRESENE,
  getClosestVALSPAR,
  getClosestBEHR,
  getClosestPPG,
  getColorCulturalMeanings,
  getColorNaturalAssociations,
  getColorSymbolicMeanings,
  getColorIndustryUsage,
  getColorSeasonalPalette,
  getClosestAUSTRALIANSTANDARD,
  getClosestPITTSBURGHPAINTS,
  getClosestGLIDDEN,
  getClosestWINSORNEWTON,
  getColorFengShui,
  getColorChakraAssociation,
  getColorGemstoneAssociations,
  getColorFoodAssociations,
  getColorHistoricalPeriod,
  getColorMarketingPsychology,
  getColorRegionalPreferences,
  getColorMaterialAssociations,
  getColorFilmPhotography,
  getColorMusicGenre,
  getColorTimeOfDay,
  getColorWeatherAssociations,
  getColorEmotionalArousal,
  getColorAccessibilityEnhanced,
  getColorReligiousSymbolism,
  getColorSeasonalLighting,
  getColorPaintFinish
} from "../lib/colorUtils";
import { useToast } from './ToastNotifications';

// Tooltip Component with proper element alignment
const Tooltip = ({ children, content, show = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);

  const handleMouseEnter = (e) => {
    if (!show) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed px-3 py-2 text-xs text-white dark:text-gray-900 bg-gray-900 dark:bg-white rounded-lg shadow-lg min-w-max max-w-sm z-[9999] pointer-events-none"
          style={{
            left: position.x,
            top: position.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="text-center leading-relaxed">{content}</div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-white"></div>
        </div>
      )}
    </>
  );
};

// Enhanced Color Info Item Component with click-to-copy and tooltips
const ColorInfoItem = ({ label, value, description }) => {
  const [copied, setCopied] = useState(false);
  const { showCopyToast } = useToast();

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Fix: Show proper toast message with actual value and label
      // Instead of: showCopyToast('Color Value', label.toLowerCase());
      showCopyToast(label, value);

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tooltipContent = `${description} • Click to copy: ${value}`;

  return (
    <Tooltip content={tooltipContent}>
      <div
        className="p-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 group border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
        onClick={() => copyToClipboard(value)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 text-sm text-gray-800 dark:text-gray-200">
            <div className="flex items-center gap-2">
              <span className="font-bold">{label}:</span>
              <Info className="w-3 h-3 text-gray-400 opacity-50 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="font-mono mt-0.5 break-all text-gray-600 dark:text-gray-400">
              {value}
            </div>
          </div>
          <div className="ml-2 flex-shrink-0">
            {copied ? (
              <Check className="w-4 h-4 text-green-600 animate-pulse" />
            ) : (
              <Copy className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors opacity-0 group-hover:opacity-100" />
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  );
};

// Panel groups organized by discipline
const PANEL_GROUPS = [
  {
    name: 'Digital & Design',
    icon: Monitor,
    panels: [
      { id: 'digital', name: 'Digital Values', description: 'Web & digital formats' },
      { id: 'design', name: 'Design Systems', description: 'Design frameworks & art supplies' }
    ]
  },
  {
    name: 'Painting',
    icon: Paintbrush,
    panels: [
      { id: 'paint', name: 'Paint Brands', description: '12 architectural paint systems' },
      { id: 'finish', name: 'Paint Finish', description: 'Sheen & finish recommendations' }
    ]
  },
  {
    name: 'Print & Production',
    icon: Printer,
    panels: [
      { id: 'print', name: 'Print Standards', description: 'CMYK, PANTONE, RAL, HKS' }
    ]
  },
  {
    name: 'Standards & Science',
    icon: Building,
    panels: [
      { id: 'government', name: 'Government & Scientific', description: 'Federal, British, Munsell, NCS' },
      { id: 'science', name: 'Color Science', description: 'LAB, OKLCH, perceptual spaces' }
    ]
  },
  {
    name: 'Accessibility',
    icon: Eye,
    panels: [
      { id: 'science', name: 'WCAG Compliance', description: 'Contrast ratios & standards', isDuplicate: true },
      { id: 'accessibility', name: 'Enhanced Accessibility', description: 'Color blindness, autism, aging' }
    ]
  },
  {
    name: 'Psychology & Marketing',
    icon: Palette,
    panels: [
      { id: 'marketing', name: 'Marketing Psychology', description: 'CTA, conversion, brand strategy' },
      { id: 'arousal', name: 'Emotional Arousal', description: 'Energy levels & activation' }
    ]
  },
  {
    name: 'Culture & Symbolism',
    icon: Info,
    panels: [
      { id: 'cultural', name: 'Cultural Meanings', description: 'Western, Eastern, universal' },
      { id: 'religious', name: 'Religious Symbolism', description: 'Cross-religious significance' }
    ]
  },
  {
    name: 'Spiritual & Energy',
    icon: Info,
    panels: [
      { id: 'fengshui', name: 'Feng Shui & Chakra', description: 'Energy, elements, balance' }
    ]
  },
  {
    name: 'Nature & Environment',
    icon: Info,
    panels: [
      { id: 'nature', name: 'Nature & Season', description: 'Natural elements & seasonal palettes' },
      { id: 'seasonal', name: 'Seasonal Lighting', description: 'How color appears across seasons' },
      { id: 'weather', name: 'Weather Moods', description: 'Meteorological associations' },
      { id: 'timeofday', name: 'Time of Day', description: 'Temporal & lighting qualities' },
      { id: 'gemstones', name: 'Gemstones & Foods', description: 'Physical associations' }
    ]
  },
  {
    name: 'Creative Media',
    icon: Info,
    panels: [
      { id: 'film', name: 'Film & Photography', description: 'Cinematic grading & film stock' },
      { id: 'music', name: 'Music Genre', description: 'Sonic aesthetics & vibes' }
    ]
  },
  {
    name: 'Design Context',
    icon: Info,
    panels: [
      { id: 'historical', name: 'Historical Periods', description: 'Design eras & movements' },
      { id: 'industry', name: 'Industry Usage', description: 'Professional applications' },
      { id: 'materials', name: 'Materials & Textures', description: 'Physical material associations' }
    ]
  }
];

// Flatten for all panel IDs (for backwards compatibility)
const PANEL_CONFIG = PANEL_GROUPS.flatMap(group =>
  group.panels.filter(panel => !panel.isDuplicate)
);

// Panel visibility toggle component
const PanelVisibilityToggle = ({ visiblePanels, onTogglePanel, onToggleAll }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const allVisible = Object.values(visiblePanels).every(v => v);
  const visibleCount = Object.values(visiblePanels).filter(v => v).length;

  // Check if all panels in a group are visible
  const isGroupVisible = (group) => {
    const panelIds = group.panels.filter(p => !p.isDuplicate).map(p => p.id);
    return panelIds.every(id => visiblePanels[id]);
  };

  // Check if all panels in a group are hidden
  const isGroupHidden = (group) => {
    const panelIds = group.panels.filter(p => !p.isDuplicate).map(p => p.id);
    return panelIds.every(id => !visiblePanels[id]);
  };

  // Toggle all panels in a group
  const toggleGroup = (group, shouldShow) => {
    const panelIds = group.panels.filter(p => !p.isDuplicate).map(p => p.id);
    panelIds.forEach(id => {
      onTogglePanel(id, shouldShow);
    });
  };

  return (
    <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Panel Visibility
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {visibleCount} of {PANEL_CONFIG.length} panels visible • Organized by discipline
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-end mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={onToggleAll}
              className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                allVisible
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
              }`}
            >
              {allVisible ? 'Hide All' : 'Show All'}
            </button>
          </div>

          <div className="space-y-2">
            {PANEL_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              const groupVisible = isGroupVisible(group);
              const groupHidden = isGroupHidden(group);

              return (
                <div key={group.name} className="space-y-1">
                  <div className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                      <GroupIcon className="w-3 h-3" />
                      <span>{group.name}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {!groupVisible && (
                        <button
                          onClick={() => toggleGroup(group, true)}
                          className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 rounded transition-colors"
                          title={`Show all ${group.name} panels`}
                        >
                          Show
                        </button>
                      )}
                      {!groupHidden && (
                        <button
                          onClick={() => toggleGroup(group, false)}
                          className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 rounded transition-colors"
                          title={`Hide all ${group.name} panels`}
                        >
                          Hide
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 ml-4">
                    {group.panels.filter(panel => !panel.isDuplicate).map((panel) => (
                      <label
                        key={panel.id}
                        className="flex items-start gap-2 p-1.5 rounded border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-all"
                      >
                        <input
                          type="checkbox"
                          checked={visiblePanels[panel.id]}
                          onChange={() => onTogglePanel(panel.id)}
                          className="mt-0.5 w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-900 dark:text-white leading-tight">
                            {panel.name}
                          </div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                            {panel.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Default color info function
const getDefaultColorInfo = (color) => {
  return {
    color,
    rgb: [0, 0, 0],
    hsl: [0, 0, 0],
    hsb: [0, 0, 0],
    lab: [0, 0, 0],
    lch: [0, 0, 0],
    oklab: [0, 0, 0],
    oklch: [0, 0, 0],
    xyz: [0, 0, 0],
    cmyk: [0, 0, 0, 0],
    colorName: "Unknown",
    cssColor: "black",
    relativeLuminance: 0,
    perceivedBrightness: 0,
    colorTemperature: "N/A",
    temperatureType: { type: "neutral", description: "Unknown temperature" },
    dominantWavelength: "N/A",
    rybHue: "0",
    contrastWithWhite: 1,
    contrastWithBlack: 1,
    wcagWhite: { ratio: "1.00", aaNormalText: "✗ AA", aaLargeText: "✗ AA Large" },
    wcagBlack: { ratio: "1.00", aaNormalText: "✗ AA", aaLargeText: "✗ AA Large" },
    accessibleAlternatives: [],
    psychology: {
      primaryColor: "Unknown",
      associations: ["neutral"],
      saturationLevel: "unknown"
    },
    harmonies: {
      monochromatic: [color, color, color, color, color],
      complementary: [color, color],
      triadic: [color, color, color]
    },
    ral: { code: "N/A", name: "Unknown" },
    hks: { code: "N/A", name: "Unknown" },
    copic: { number: "N/A", name: "Unknown" },
    pantone: "Unknown",
    ikea: { code: "N/A", name: "Unknown" },
    prismacolor: { number: "N/A", name: "Unknown" },
    ncs: { ncs: "N/A", name: "Unknown" },
    federalStandard: { fs: "N/A", name: "Unknown" },
    britishStandard: { bs: "N/A", name: "Unknown" },
    munsell: { munsell: "N/A", name: "Unknown" },
    automotive: { code: "N/A", brand: "Unknown", name: "Unknown" },
    crayola: { name: "Unknown", hex: "#000000" },
    sherwinWilliams: { code: "N/A", name: "Unknown" },
    benjaminMoore: { code: "N/A", name: "Unknown" },
    dulux: { code: "N/A", name: "Unknown" },
    resene: { code: "N/A", name: "Unknown" },
    valspar: { code: "N/A", name: "Unknown" },
    behr: { code: "N/A", name: "Unknown" },
    ppg: { code: "N/A", name: "Unknown" },
    australianStandard: { code: "N/A", name: "Unknown" },
    pittsburghPaints: { code: "N/A", name: "Unknown" },
    glidden: { code: "N/A", name: "Unknown" },
    winsorNewton: { code: "N/A", name: "Unknown" },
    materialDesign: { color: "grey", shade: "500" },
    tailwind: { color: "gray", shade: "500" },
    culturalMeanings: { western: [], eastern: [], general: [] },
    naturalAssociations: [],
    symbolicMeanings: [],
    industryUsage: [],
    seasonalPalette: { season: "Unknown", description: "", characteristics: [], undertone: "neutral", intensity: "muted", value: "medium" },
    fengShui: { element: "Unknown", energy: "Unknown", rooms: [], direction: "Unknown", meaning: "Unknown" },
    chakra: { chakra: "Unknown", sanskrit: "Unknown", location: "Unknown", meaning: "Unknown", qualities: [] },
    gemstones: [],
    foods: [],
    historicalPeriods: [],
    marketingPsychology: { callToAction: "Unknown", conversionImpact: "Unknown", brandPersonality: [], targetAudience: "Unknown", avoidUsing: "Unknown" },
    regionalPreferences: [],
    materials: { materials: [], textures: [] },
    filmPhotography: { cinematicStyle: "Unknown", moodBoarding: [], filmStockAssociation: "Unknown", gradingTechnique: "Unknown" },
    musicGenre: { genres: [], vibe: "Unknown" },
    timeOfDay: { timeOfDay: "Unknown", lighting: "Unknown", mood: "Unknown" },
    weatherAssociations: { weather: [], atmosphere: "Unknown" },
    emotionalArousal: { arousalLevel: "Unknown", energyRating: 5, effect: "Unknown", useCases: [] },
    accessibilityEnhanced: { colorBlindFriendly: "Unknown", autismFriendly: "Unknown", ageConsideration: "Unknown", lowVisionSupport: "Unknown" },
    religiousSymbolism: { christianity: "", islam: "", hinduism: "", buddhism: "", judaism: "" },
    seasonalLighting: { springLight: "", summerLight: "", autumnLight: "", winterLight: "" },
    paintFinish: { recommendedFinish: "Unknown", matteSuitability: "Unknown", glossSuitability: "Unknown", satinSuitability: "Unknown", specialFinishes: [], roomRecommendations: [] }
  };
};

const SelectedColorDisplay = React.memo(({ color }) => {
  // Define calculateColorInfo first, before using it
  const calculateColorInfo = React.useCallback((color) => {
    try {
      const rgb = hexToRgb(color);
      const hsl = hexToHsl(color);
      const hsb = rgbToHSB(rgb[0], rgb[1], rgb[2]);
      const lab = hexToLAB(color);
      const lch = hexToLCH(color);
      const oklab = hexToOKLAB(color);
      const oklch = hexToOKLCH(color);
      const xyz = rgbToXYZ(rgb[0], rgb[1], rgb[2]);

      // Color identification
      const colorName = getWadaColorName(hsl[0], hsl[1], hsl[2]);
      const cssColor = getClosestCSSColor(color);

      // Color science
      const relativeLuminance = getRelativeLuminance(rgb[0], rgb[1], rgb[2]);
      const perceivedBrightness = getPerceivedBrightness(rgb[0], rgb[1], rgb[2]);
      const cmyk = rgbToCMYK(rgb[0], rgb[1], rgb[2]);
      const colorTemperature = getColorTemperature(rgb[0], rgb[1], rgb[2]);
      const temperatureType = getColorTemperatureType(color);
      const dominantWavelength = getDominantWavelength(rgb[0], rgb[1], rgb[2]);

      // Accessibility
      const whiteRgb = [255, 255, 255];
      const blackRgb = [0, 0, 0];
      const contrastWithWhite = getWCAGContrastRatio(rgb, whiteRgb);
      const contrastWithBlack = getWCAGContrastRatio(rgb, blackRgb);
      const wcagWhite = getWCAGCompliance(contrastWithWhite);
      const wcagBlack = getWCAGCompliance(contrastWithBlack);
      const accessibleAlternatives = getAccessibleAlternatives(color);

      // Color psychology
      const psychology = getColorPsychology(color);
      const harmonies = getColorHarmonies(color);

      // Physical Standards - Existing
      const ral = getClosestRAL(rgb[0], rgb[1], rgb[2]);
      const hks = getClosestHKS(rgb[0], rgb[1], rgb[2]);
      const copic = getClosestCOPIC(rgb[0], rgb[1], rgb[2]);
      const pantone = getClosestPANTONE(rgb[0], rgb[1], rgb[2]);
      const ikea = getClosestIKEA(rgb[0], rgb[1], rgb[2]);
      const prismacolor = getClosestPRISMACOLOR(rgb[0], rgb[1], rgb[2]);

      // Physical Standards - Government & Scientific
      const ncs = getClosestNCS(rgb[0], rgb[1], rgb[2]);
      const federalStandard = getClosestFEDERALSTANDARD(rgb[0], rgb[1], rgb[2]);
      const britishStandard = getClosestBRITISHSTANDARD(rgb[0], rgb[1], rgb[2]);
      const munsell = getClosestMUNSELL(rgb[0], rgb[1], rgb[2]);
      const automotive = getClosestAUTOMOTIVE(rgb[0], rgb[1], rgb[2]);

      // Paint Systems
      const crayola = getClosestCRAYOLA(rgb[0], rgb[1], rgb[2]);
      const sherwinWilliams = getClosestSHERWINWILLIAMS(rgb[0], rgb[1], rgb[2]);
      const benjaminMoore = getClosestBENJAMINMOORE(rgb[0], rgb[1], rgb[2]);
      const dulux = getClosestDULUX(rgb[0], rgb[1], rgb[2]);
      const resene = getClosestRESENE(rgb[0], rgb[1], rgb[2]);
      const valspar = getClosestVALSPAR(rgb[0], rgb[1], rgb[2]);
      const behr = getClosestBEHR(rgb[0], rgb[1], rgb[2]);
      const ppg = getClosestPPG(rgb[0], rgb[1], rgb[2]);
      const australianStandard = getClosestAUSTRALIANSTANDARD(rgb[0], rgb[1], rgb[2]);
      const pittsburghPaints = getClosestPITTSBURGHPAINTS(rgb[0], rgb[1], rgb[2]);
      const glidden = getClosestGLIDDEN(rgb[0], rgb[1], rgb[2]);
      const winsorNewton = getClosestWINSORNEWTON(rgb[0], rgb[1], rgb[2]);

      // Design Systems
      const materialDesign = getClosestMATERIALDESIGN(rgb[0], rgb[1], rgb[2]);
      const tailwind = getClosestTAILWIND(rgb[0], rgb[1], rgb[2]);

      // Enhanced Cultural & Conceptual Data
      const culturalMeanings = getColorCulturalMeanings(color);
      const naturalAssociations = getColorNaturalAssociations(color);
      const symbolicMeanings = getColorSymbolicMeanings(color);
      const industryUsage = getColorIndustryUsage(color);
      const seasonalPalette = getColorSeasonalPalette(color);

      // Enhanced Analysis Data
      const fengShui = getColorFengShui(color);
      const chakra = getColorChakraAssociation(color);
      const gemstones = getColorGemstoneAssociations(color);
      const foods = getColorFoodAssociations(color);
      const historicalPeriods = getColorHistoricalPeriod(color);
      const marketingPsychology = getColorMarketingPsychology(color);
      const regionalPreferences = getColorRegionalPreferences(color);
      const materials = getColorMaterialAssociations(color);

      // New Enhanced Analysis Data
      const filmPhotography = getColorFilmPhotography(color);
      const musicGenre = getColorMusicGenre(color);
      const timeOfDay = getColorTimeOfDay(color);
      const weatherAssociations = getColorWeatherAssociations(color);
      const emotionalArousal = getColorEmotionalArousal(color);
      const accessibilityEnhanced = getColorAccessibilityEnhanced(color);
      const religiousSymbolism = getColorReligiousSymbolism(color);
      const seasonalLighting = getColorSeasonalLighting(color);
      const paintFinish = getColorPaintFinish(color);

      // Calculate RYB hue
      const rybHue = (() => {
        const [r, g, b] = rgb;
        const hue = Math.atan2(Math.sqrt(3) * (g - b), 2 * r - g - b);
        return (((hue * 180) / Math.PI + 360) % 360).toFixed(2);
      })();

      return {
        // Basic color data
        color,
        rgb,
        hsl,
        hsb,

        // Advanced color spaces
        lab,
        lch,
        oklab,
        oklch,
        xyz,
        cmyk,

        // Color identification
        colorName,
        cssColor,

        // Color science
        relativeLuminance,
        perceivedBrightness,
        colorTemperature,
        temperatureType,
        dominantWavelength,
        rybHue,

        // Accessibility
        contrastWithWhite,
        contrastWithBlack,
        wcagWhite,
        wcagBlack,
        accessibleAlternatives,

        // Psychology and harmonies
        psychology,
        harmonies,

        // Physical standards
        ral,
        hks,
        copic,
        pantone,
        ikea,
        prismacolor,
        ncs,
        federalStandard,
        britishStandard,
        munsell,
        automotive,

        // Paint systems
        crayola,
        sherwinWilliams,
        benjaminMoore,
        dulux,
        resene,
        valspar,
        behr,
        ppg,
        australianStandard,
        pittsburghPaints,
        glidden,
        winsorNewton,

        // Design systems
        materialDesign,
        tailwind,

        // Enhanced cultural & conceptual data
        culturalMeanings,
        naturalAssociations,
        symbolicMeanings,
        industryUsage,
        seasonalPalette,

        // Enhanced analysis data
        fengShui,
        chakra,
        gemstones,
        foods,
        historicalPeriods,
        marketingPsychology,
        regionalPreferences,
        materials,

        // New enhanced analysis data
        filmPhotography,
        musicGenre,
        timeOfDay,
        weatherAssociations,
        emotionalArousal,
        accessibilityEnhanced,
        religiousSymbolism,
        seasonalLighting,
        paintFinish,
      };
    } catch (error) {
      console.error("Error calculating color info:", error);
      return getDefaultColorInfo(color);
    }
  }, []);

  const [colorInfo, setColorInfo] = useState(() => calculateColorInfo(color));

  // Initialize panel visibility from localStorage or default to all visible
  const [visiblePanels, setVisiblePanels] = useState(() => {
    try {
      const saved = localStorage.getItem('visibleColorPanels');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading panel visibility:', error);
    }
    // Default: all panels visible
    return PANEL_CONFIG.reduce((acc, panel) => {
      acc[panel.id] = true;
      return acc;
    }, {});
  });

  useEffect(() => {
    setColorInfo(calculateColorInfo(color));
  }, [color, calculateColorInfo]);

  // Save panel visibility to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('visibleColorPanels', JSON.stringify(visiblePanels));
    } catch (error) {
      console.error('Error saving panel visibility:', error);
    }
  }, [visiblePanels]);

  // Toggle individual panel
  const handleTogglePanel = (panelId) => {
    setVisiblePanels(prev => ({
      ...prev,
      [panelId]: !prev[panelId]
    }));
  };

  // Toggle all panels
  const handleToggleAll = () => {
    const allVisible = Object.values(visiblePanels).every(v => v);
    const newVisibility = PANEL_CONFIG.reduce((acc, panel) => {
      acc[panel.id] = !allVisible;
      return acc;
    }, {});
    setVisiblePanels(newVisibility);
  };

  if (!colorInfo) {
    return (
      <div className="mt-8 mb-6">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Select a color to see detailed information
        </div>
      </div>
    );
  }

  return (
    <div data-color-info className="mt-8 mb-6">
      {/* Always Visible: Selected Color Preview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center gap-4 p-6">
          <div
            className="w-16 h-16 rounded-xl border-2 border-white shadow-lg flex-shrink-0"
            style={{ backgroundColor: colorInfo.color }}
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
              {colorInfo.colorName}
            </h3>
            <p className="text-base text-gray-600 dark:text-gray-400 font-mono">
              {colorInfo.color}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              {colorInfo.psychology.primaryColor} • {colorInfo.temperatureType.type} • {colorInfo.psychology.saturationLevel} saturation
            </p>
          </div>
        </div>

        {/* Quick Access Row - Always visible */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="grid grid-cols-3 gap-4 pt-4">
            <div className="text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">RGB</div>
              <div className="font-mono text-sm text-gray-900 dark:text-white">
                {colorInfo.rgb.join(", ")}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">HSL</div>
              <div className="font-mono text-sm text-gray-900 dark:text-white">
                {colorInfo.hsl[0]}°, {colorInfo.hsl[1]}%, {colorInfo.hsl[2]}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">WCAG</div>
              <div className="text-sm text-gray-900 dark:text-white">
                {colorInfo.wcagWhite.aaNormalText}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Panel Visibility Toggle */}
      <div className="mt-6">
        <PanelVisibilityToggle
          visiblePanels={visiblePanels}
          onTogglePanel={handleTogglePanel}
          onToggleAll={handleToggleAll}
        />
      </div>

      {/* Always Visible: Detailed Information */}
      <div className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Panel 1: Digital Values */}
          {visiblePanels.digital && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Digital Values</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Web & digital formats</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="HEX" value={colorInfo.color} description="Hexadecimal color code" />
              <ColorInfoItem label="RGB" value={`${colorInfo.rgb[0]}, ${colorInfo.rgb[1]}, ${colorInfo.rgb[2]}`} description="Red, Green, Blue (0-255)" />
              <ColorInfoItem label="HSL" value={`${colorInfo.hsl[0]}°, ${colorInfo.hsl[1]}%, ${colorInfo.hsl[2]}%`} description="Hue, Saturation, Lightness" />
              <ColorInfoItem label="HSB/HSV" value={`${colorInfo.hsb[0]}°, ${colorInfo.hsb[1]}%, ${colorInfo.hsb[2]}%`} description="Hue, Saturation, Brightness/Value" />
              <ColorInfoItem label="CSS rgb()" value={`rgb(${colorInfo.rgb[0]}, ${colorInfo.rgb[1]}, ${colorInfo.rgb[2]})`} description="CSS RGB function" />
              <ColorInfoItem label="CSS hsl()" value={`hsl(${colorInfo.hsl[0]}, ${colorInfo.hsl[1]}%, ${colorInfo.hsl[2]}%)`} description="CSS HSL function" />
              <ColorInfoItem label="CSS Named" value={colorInfo.cssColor} description="Closest CSS named color" />
              <ColorInfoItem label="RGB Integer" value={`${parseInt(colorInfo.color.slice(1), 16)}`} description="Decimal RGB integer value" />
              <ColorInfoItem label="Android ARGB" value={`0xFF${colorInfo.color.slice(1).toUpperCase()}`} description="Android color format (ARGB)" />
              <ColorInfoItem label="iOS UIColor" value={`UIColor(red: ${(colorInfo.rgb[0]/255).toFixed(3)}, green: ${(colorInfo.rgb[1]/255).toFixed(3)}, blue: ${(colorInfo.rgb[2]/255).toFixed(3)}, alpha: 1.0)`} description="iOS/Swift UIColor initializer" />
              <ColorInfoItem label="Unity Color" value={`new Color(${(colorInfo.rgb[0]/255).toFixed(3)}f, ${(colorInfo.rgb[1]/255).toFixed(3)}f, ${(colorInfo.rgb[2]/255).toFixed(3)}f)`} description="Unity3D Color constructor" />
              <ColorInfoItem label="Wavelength" value={colorInfo.dominantWavelength} description="Dominant wavelength in nanometers" />
            </div>
          </div>
          )}

          {/* Panel 2: Print & Production */}
          {visiblePanels.print && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <Printer className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Print & Production</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Industry printing standards</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="CMYK" value={`${colorInfo.cmyk[0]}%, ${colorInfo.cmyk[1]}%, ${colorInfo.cmyk[2]}%, ${colorInfo.cmyk[3]}%`} description="Cyan, Magenta, Yellow, Key (Black)" />
              <ColorInfoItem label="CMYK Function" value={`cmyk(${colorInfo.cmyk[0]}%, ${colorInfo.cmyk[1]}%, ${colorInfo.cmyk[2]}%, ${colorInfo.cmyk[3]}%)`} description="CSS cmyk() function format" />
              <ColorInfoItem label="PANTONE" value={colorInfo.pantone} description="Industry standard for printing" />
              <ColorInfoItem label="RAL" value={`${colorInfo.ral.code} (${colorInfo.ral.name})`} description="European color standard" />
              <ColorInfoItem label="HKS" value={`${colorInfo.hks.code} (${colorInfo.hks.name})`} description="German printing standard" />
              <ColorInfoItem label="NCS" value={`${colorInfo.ncs.ncs} (${colorInfo.ncs.name})`} description="Natural Color System (Scandinavia)" />
              <ColorInfoItem label="LAB" value={`L: ${colorInfo.lab[0].toFixed(1)}, a: ${colorInfo.lab[1].toFixed(1)}, b: ${colorInfo.lab[2].toFixed(1)}`} description="CIE LAB - Perceptual color space" />
              <ColorInfoItem label="LCH" value={`L: ${colorInfo.lch[0].toFixed(1)}, C: ${colorInfo.lch[1].toFixed(1)}, H: ${colorInfo.lch[2].toFixed(0)}°`} description="LCH - Cylindrical LAB" />
              <ColorInfoItem label="XYZ" value={`X: ${colorInfo.xyz[0].toFixed(2)}, Y: ${colorInfo.xyz[1].toFixed(2)}, Z: ${colorInfo.xyz[2].toFixed(2)}`} description="CIE XYZ tristimulus values" />
              <ColorInfoItem label="Ink Coverage" value={`${(colorInfo.cmyk[0] + colorInfo.cmyk[1] + colorInfo.cmyk[2] + colorInfo.cmyk[3])}%`} description="Total ink coverage (TAC)" />
              <ColorInfoItem label="Dot Gain" value={colorInfo.cmyk[3] > 80 ? "High (rich blacks)" : colorInfo.cmyk[3] > 40 ? "Medium" : "Low"} description="Expected dot gain in offset printing" />
              <ColorInfoItem label="Temperature" value={colorInfo.colorTemperature} description="Color temperature in Kelvin" />
            </div>
          </div>
          )}

          {/* Panel 3: Paint Systems */}
          {visiblePanels.paint && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <Paintbrush className="w-4 h-4 text-green-600 dark:text-green-400" />
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Paint Systems (12 brands)</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Architectural & consumer paints</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Sherwin-Williams" value={`${colorInfo.sherwinWilliams.code} (${colorInfo.sherwinWilliams.name})`} description="Sherwin-Williams paint colors" />
              <ColorInfoItem label="Benjamin Moore" value={`${colorInfo.benjaminMoore.code} (${colorInfo.benjaminMoore.name})`} description="Benjamin Moore paint colors" />
              <ColorInfoItem label="Behr" value={`${colorInfo.behr.code} (${colorInfo.behr.name})`} description="Behr paint colors" />
              <ColorInfoItem label="Valspar" value={`${colorInfo.valspar.code} (${colorInfo.valspar.name})`} description="Valspar paint colors" />
              <ColorInfoItem label="PPG" value={`${colorInfo.ppg.code} (${colorInfo.ppg.name})`} description="PPG paint colors" />
              <ColorInfoItem label="Pittsburgh Paints" value={`${colorInfo.pittsburghPaints.code} (${colorInfo.pittsburghPaints.name})`} description="Pittsburgh Paints colors" />
              <ColorInfoItem label="Glidden" value={`${colorInfo.glidden.code} (${colorInfo.glidden.name})`} description="Glidden paint colors" />
              <ColorInfoItem label="Dulux" value={`${colorInfo.dulux.code} (${colorInfo.dulux.name})`} description="Dulux paint colors" />
              <ColorInfoItem label="Resene" value={`${colorInfo.resene.code} (${colorInfo.resene.name})`} description="Resene paint colors" />
              <ColorInfoItem label="Australian Std" value={`${colorInfo.australianStandard.code} (${colorInfo.australianStandard.name})`} description="Australian Standard AS 2700" />
              <ColorInfoItem label="Winsor & Newton" value={`${colorInfo.winsorNewton.code} (${colorInfo.winsorNewton.name})`} description="Winsor & Newton watercolors" />
              <ColorInfoItem label="Crayola" value={colorInfo.crayola.name} description="Crayola crayon colors" />
            </div>
          </div>
          )}

          {/* Panel 4: Government & Scientific */}
          {visiblePanels.government && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Government & Scientific</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Official specifications & notation</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Federal Standard" value={`${colorInfo.federalStandard.fs} (${colorInfo.federalStandard.name})`} description="US Federal Standard 595" />
              <ColorInfoItem label="British Standard" value={`${colorInfo.britishStandard.bs} (${colorInfo.britishStandard.name})`} description="BS 381C British Standard" />
              <ColorInfoItem label="Munsell" value={`${colorInfo.munsell.munsell} (${colorInfo.munsell.name})`} description="Scientific color notation" />
              <ColorInfoItem label="NCS" value={`${colorInfo.ncs.ncs} (${colorInfo.ncs.name})`} description="Natural Color System" />
            </div>
          </div>

          )}

          {/* Panel 5: Design & Art */}
          {visiblePanels.design && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Design & Art</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Design systems & art supplies</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Tailwind CSS" value={`${colorInfo.tailwind.color}-${colorInfo.tailwind.shade}`} description="Tailwind color palette" />
              <ColorInfoItem label="Material Design" value={`${colorInfo.materialDesign.color} ${colorInfo.materialDesign.shade}`} description="Google's design system" />
              <ColorInfoItem label="COPIC" value={`${colorInfo.copic.number} (${colorInfo.copic.name})`} description="COPIC marker colors" />
              <ColorInfoItem label="PRISMACOLOR" value={`${colorInfo.prismacolor.number} (${colorInfo.prismacolor.name})`} description="Prismacolor pencils" />
              <ColorInfoItem label="Automotive" value={`${colorInfo.automotive.brand} ${colorInfo.automotive.code} (${colorInfo.automotive.name})`} description="Car paint color code" />
              <ColorInfoItem label="IKEA" value={`${colorInfo.ikea.code} (${colorInfo.ikea.name})`} description="IKEA furniture colors" />
            </div>
          </div>

          )}

          {/* Panel 6: Color Science & Accessibility */}
          {visiblePanels.science && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Color Science & Accessibility</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Perceptual & WCAG compliance</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="LAB" value={`${colorInfo.lab[0].toFixed(1)}, ${colorInfo.lab[1].toFixed(1)}, ${colorInfo.lab[2].toFixed(1)}`} description="CIE LAB color space" />
              <ColorInfoItem label="OKLCH" value={`${colorInfo.oklch[0].toFixed(2)}, ${colorInfo.oklch[1].toFixed(2)}, ${colorInfo.oklch[2].toFixed(0)}°`} description="Modern perceptual color space" />
              <ColorInfoItem label="Luminance" value={colorInfo.relativeLuminance.toFixed(3)} description="Relative brightness (WCAG)" />
              <ColorInfoItem label="WCAG vs White" value={colorInfo.wcagWhite.aaNormalText} description={`Contrast ratio: ${colorInfo.wcagWhite.ratio}:1`} />
              <ColorInfoItem label="WCAG vs Black" value={colorInfo.wcagBlack.aaNormalText} description={`Contrast ratio: ${colorInfo.wcagBlack.ratio}:1`} />
              <ColorInfoItem label="Psychology" value={colorInfo.psychology.primaryColor} description={colorInfo.psychology.associations?.slice(0, 3).join(", ") || "Color associations"} />
            </div>
          </div>

          )}

          {/* Panel 7: Cultural & Symbolic */}
          {visiblePanels.cultural && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Cultural & Symbolic</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Cross-cultural meanings & symbolism</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Western Culture" value={colorInfo.culturalMeanings.western.slice(0, 3).join(", ")} description="Western cultural associations" />
              <ColorInfoItem label="Eastern Culture" value={colorInfo.culturalMeanings.eastern.slice(0, 3).join(", ")} description="Eastern cultural associations" />
              <ColorInfoItem label="Universal" value={colorInfo.culturalMeanings.general.join(", ")} description="Cross-cultural meanings" />
              <ColorInfoItem label="Symbolic Meanings" value={colorInfo.symbolicMeanings.slice(0, 4).join(", ")} description="Symbolic associations and concepts" />
            </div>
          </div>

          )}

          {/* Panel 8: Nature & Season */}
          {visiblePanels.nature && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Nature & Season</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Natural associations & seasonal analysis</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Natural Elements" value={colorInfo.naturalAssociations.join(", ")} description="Things in nature with this color" />
              <ColorInfoItem label="Season" value={colorInfo.seasonalPalette.season} description={colorInfo.seasonalPalette.description} />
              <ColorInfoItem label="Characteristics" value={colorInfo.seasonalPalette.characteristics.join(", ")} description="Seasonal palette characteristics" />
              <ColorInfoItem label="Undertone" value={`${colorInfo.seasonalPalette.undertone} / ${colorInfo.seasonalPalette.intensity}`} description={`${colorInfo.seasonalPalette.value} value`} />
            </div>
          </div>

          )}

          {/* Panel 9: Industry & Usage */}
          {visiblePanels.industry && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Industry & Usage</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Common industry applications</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Primary Industries" value={colorInfo.industryUsage.slice(0, 3).join(", ")} description="Top industries using this color" />
              <ColorInfoItem label="Secondary Industries" value={colorInfo.industryUsage.slice(3, 5).join(", ") || "Various"} description="Additional industry applications" />
              <ColorInfoItem label="Brand Psychology" value={colorInfo.psychology.primaryColor} description={`Evokes ${colorInfo.psychology.associations.slice(0, 3).join(", ")}`} />
            </div>
          </div>

          )}

          {/* Panel 10: Feng Shui & Chakra */}
          {visiblePanels.fengshui && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Feng Shui & Chakra</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Energy & spiritual associations</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Feng Shui Element" value={colorInfo.fengShui.element} description={colorInfo.fengShui.meaning} />
              <ColorInfoItem label="Energy Type" value={colorInfo.fengShui.energy} description={`Best direction: ${colorInfo.fengShui.direction}`} />
              <ColorInfoItem label="Recommended Rooms" value={colorInfo.fengShui.rooms.join(", ")} description="Feng Shui placement suggestions" />
              <ColorInfoItem label="Chakra" value={`${colorInfo.chakra.chakra} (${colorInfo.chakra.sanskrit})`} description={`${colorInfo.chakra.location} • ${colorInfo.chakra.meaning}`} />
              <ColorInfoItem label="Chakra Qualities" value={colorInfo.chakra.qualities.join(", ")} description="Energy center qualities" />
            </div>
          </div>

          )}

          {/* Panel 11: Gemstones & Foods */}
          {visiblePanels.gemstones && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-rose-600 dark:text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Gemstones & Foods</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Physical associations with this color</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Gemstones" value={colorInfo.gemstones.join(", ")} description="Crystals and gemstones in this color" />
              <ColorInfoItem label="Foods" value={colorInfo.foods.join(", ")} description="Foods with this color" />
            </div>
          </div>

          )}

          {/* Panel 12: Historical & Regional */}
          {visiblePanels.historical && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Historical & Regional</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Design history & regional aesthetics</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Historical Periods" value={colorInfo.historicalPeriods.join(", ")} description="Design eras associated with this color" />
              <ColorInfoItem label="Regional Styles" value={colorInfo.regionalPreferences.join(", ")} description="Geographic design preferences" />
            </div>
          </div>

          )}

          {/* Panel 13: Marketing Psychology */}
          {visiblePanels.marketing && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Marketing Psychology</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Brand strategy & conversion metrics</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="CTA Effectiveness" value={colorInfo.marketingPsychology.callToAction} description="Call-to-action performance" />
              <ColorInfoItem label="Conversion Impact" value={colorInfo.marketingPsychology.conversionImpact} description="Purchase behavior influence" />
              <ColorInfoItem label="Brand Personality" value={colorInfo.marketingPsychology.brandPersonality.join(", ")} description="Brand traits conveyed" />
              <ColorInfoItem label="Target Audience" value={colorInfo.marketingPsychology.targetAudience} description="Ideal demographic" />
              <ColorInfoItem label="Avoid Using For" value={colorInfo.marketingPsychology.avoidUsing} description="Poor fit categories" />
            </div>
          </div>

          )}

          {/* Panel 14: Materials & Textures */}
          {visiblePanels.materials && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Materials & Textures</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Physical material associations</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Materials" value={colorInfo.materials.materials.join(", ")} description="Physical materials in this color" />
              <ColorInfoItem label="Textures" value={colorInfo.materials.textures.join(", ")} description="Texture qualities" />
            </div>
          </div>

          )}

          {/* Panel 15: Film & Photography */}
          {visiblePanels.film && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Film & Photography</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Cinematic color grading & film aesthetics</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Cinematic Style" value={colorInfo.filmPhotography.cinematicStyle} description="Film genre and look classification" />
              <ColorInfoItem label="Mood Boarding" value={colorInfo.filmPhotography.moodBoarding.join(", ")} description="Visual mood and atmosphere" />
              <ColorInfoItem label="Film Stock" value={colorInfo.filmPhotography.filmStockAssociation} description="Traditional film stock characteristics" />
              <ColorInfoItem label="Grading Technique" value={colorInfo.filmPhotography.gradingTechnique} description="Color grading methodology" />
            </div>
          </div>

          )}

          {/* Panel 16: Music Genre */}
          {visiblePanels.music && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-pink-600 dark:text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Music Genre</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Musical associations & sonic aesthetics</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Genres" value={colorInfo.musicGenre.genres.join(", ")} description="Music genres associated with this color" />
              <ColorInfoItem label="Vibe" value={colorInfo.musicGenre.vibe} description="Overall musical energy and feeling" />
            </div>
          </div>

          )}

          {/* Panel 17: Time of Day */}
          {visiblePanels.timeofday && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Time of Day</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Temporal associations & lighting</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Time Period" value={colorInfo.timeOfDay.timeOfDay} description="Associated time of day" />
              <ColorInfoItem label="Lighting Quality" value={colorInfo.timeOfDay.lighting} description="Natural light characteristics" />
              <ColorInfoItem label="Mood" value={colorInfo.timeOfDay.mood} description="Emotional atmosphere of this time" />
            </div>
          </div>

          )}

          {/* Panel 18: Weather Associations */}
          {visiblePanels.weather && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-sky-600 dark:text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Weather Associations</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Meteorological & atmospheric moods</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Weather Conditions" value={colorInfo.weatherAssociations.weather.join(", ")} description="Associated weather patterns" />
              <ColorInfoItem label="Atmosphere" value={colorInfo.weatherAssociations.atmosphere} description="Environmental mood and feeling" />
            </div>
          </div>

          )}

          {/* Panel 19: Emotional Arousal */}
          {visiblePanels.arousal && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Emotional Arousal</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Psychological activation & energy level</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Arousal Level" value={colorInfo.emotionalArousal.arousalLevel} description="Psychological stimulation intensity" />
              <ColorInfoItem label="Energy Rating" value={`${colorInfo.emotionalArousal.energyRating}/10`} description="Numerical energy intensity (1-10)" />
              <ColorInfoItem label="Psychological Effect" value={colorInfo.emotionalArousal.effect} description="Impact on mental state" />
              <ColorInfoItem label="Best Use Cases" value={colorInfo.emotionalArousal.useCases.join(", ")} description="Recommended applications" />
            </div>
          </div>

          )}

          {/* Panel 20: Enhanced Accessibility */}
          {visiblePanels.accessibility && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Enhanced Accessibility</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Beyond WCAG - comprehensive vision analysis</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Color Blind Friendly" value={colorInfo.accessibilityEnhanced.colorBlindFriendly} description="Deuteranopia & protanopia visibility" />
              <ColorInfoItem label="Autism Friendly" value={colorInfo.accessibilityEnhanced.autismFriendly} description="Sensory processing considerations" />
              <ColorInfoItem label="Age Consideration" value={colorInfo.accessibilityEnhanced.ageConsideration} description="Visibility for older adults" />
              <ColorInfoItem label="Low Vision Support" value={colorInfo.accessibilityEnhanced.lowVisionSupport} description="High contrast and magnification suitability" />
            </div>
          </div>

          )}

          {/* Panel 21: Religious Symbolism */}
          {visiblePanels.religious && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Religious Symbolism</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Cross-religious color meanings</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Christianity" value={colorInfo.religiousSymbolism.christianity || "No specific meaning"} description="Christian liturgical and symbolic significance" />
              <ColorInfoItem label="Islam" value={colorInfo.religiousSymbolism.islam || "No specific meaning"} description="Islamic cultural and religious associations" />
              <ColorInfoItem label="Hinduism" value={colorInfo.religiousSymbolism.hinduism || "No specific meaning"} description="Hindu deities and spiritual concepts" />
              <ColorInfoItem label="Buddhism" value={colorInfo.religiousSymbolism.buddhism || "No specific meaning"} description="Buddhist teachings and symbolism" />
              <ColorInfoItem label="Judaism" value={colorInfo.religiousSymbolism.judaism || "No specific meaning"} description="Jewish traditions and sacred meanings" />
            </div>
          </div>

          )}

          {/* Panel 22: Seasonal Lighting */}
          {visiblePanels.seasonal && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Seasonal Lighting</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">How color appears in different seasons</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Spring Light" value={colorInfo.seasonalLighting.springLight || "Neutral appearance"} description="How this color appears in spring daylight" />
              <ColorInfoItem label="Summer Light" value={colorInfo.seasonalLighting.summerLight || "Neutral appearance"} description="How this color appears in summer daylight" />
              <ColorInfoItem label="Autumn Light" value={colorInfo.seasonalLighting.autumnLight || "Neutral appearance"} description="How this color appears in autumn daylight" />
              <ColorInfoItem label="Winter Light" value={colorInfo.seasonalLighting.winterLight || "Neutral appearance"} description="How this color appears in winter daylight" />
            </div>
          </div>

          )}

          {/* Panel 23: Paint Finish */}
          {visiblePanels.finish && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-lime-600 dark:text-lime-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Paint Finish</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Recommended finish & sheen for this color</p>
            </div>
            <div className="p-3 space-y-1">
              <ColorInfoItem label="Recommended Finish" value={colorInfo.paintFinish.recommendedFinish} description="Best overall paint sheen for this color" />
              <ColorInfoItem label="Matte Suitability" value={colorInfo.paintFinish.matteSuitability} description="How this color performs in flat/matte finish" />
              <ColorInfoItem label="Satin Suitability" value={colorInfo.paintFinish.satinSuitability} description="How this color performs in satin/eggshell finish" />
              <ColorInfoItem label="Gloss Suitability" value={colorInfo.paintFinish.glossSuitability} description="How this color performs in semi/high gloss" />
              <ColorInfoItem label="Special Finishes" value={colorInfo.paintFinish.specialFinishes.join(", ")} description="Alternative finish recommendations" />
              <ColorInfoItem label="Room Recommendations" value={colorInfo.paintFinish.roomRecommendations.join(", ")} description="Best rooms for this color and finish" />
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default SelectedColorDisplay;